import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  createRouter, deleteRouter, fetchRouters, newRouter, Router, updateRouter,
} from '../features/routers/routers';
import { FIELD_LABELS, diffRouters, validateRouter } from '../features/routers/routerFields';
import { ROUTER_NETWORKS, networkOfRouter } from '../features/routers/routerNetwork';
import { RootState } from '../app/store';

/**
 * One router, edited on its own page. Nothing is written until the operator
 * reviews the changes in the confirmation dialog and confirms, which is how
 * a plan is edited: the list itself can no longer mutate anything.
 */
const RouterCard = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { routerId } = useParams();
  const routersState = useAppSelector((state: RootState) => state.routers);

  const isNew = routerId === undefined;
  const decodedId = routerId ? decodeURIComponent(routerId) : '';

  const [form, setForm] = useState<Router | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { dispatch(fetchRouters()); }, []);

  const original = isNew
    ? newRouter()
    : (routersState.value ?? []).find((r) => r.id === decodedId);

  useEffect(() => {
    setForm(original ? { ...original } : null);
  }, [original?.id, routersState.status]);

  if (routersState.status === 'loading' && !form) {
    return (
      <Box sx={{
        display: 'flex', justifyContent: 'center', p: 10, mt: 10,
      }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!original || !form) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3, maxWidth: 900, mx: 'auto', mt: 10,
        }}
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          Router &quot;
          {decodedId}
          &quot; was not found.
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/routers')}>
          Back to routers
        </Button>
      </Paper>
    );
  }

  const changes = diffRouters(original, form);
  const errors = validateRouter(form, isNew);
  const dirty = isNew || changes.length > 0;

  const setField = <K extends keyof Router>(field: K, value: Router[K]) => {
    setForm({ ...form, [field]: value });
  };

  const textField = (field: keyof Router, extra: Record<string, unknown> = {}) => (
    <TextField
      label={FIELD_LABELS[field]}
      value={form[field] ?? ''}
      onChange={(e) => setField(field, e.target.value as Router[typeof field])}
      fullWidth
      {...extra}
    />
  );

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        await dispatch(createRouter(form)).unwrap();
      } else {
        await dispatch(updateRouter(form)).unwrap();
      }
      navigate('/dashboard/routers');
    } catch (e) {
      setError(`Failed to save router: ${e instanceof Error ? e.message : String(e)}`);
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    setError(null);
    try {
      await dispatch(deleteRouter(original)).unwrap();
      navigate('/dashboard/routers');
    } catch (e) {
      setError(`Failed to delete router: ${e instanceof Error ? e.message : String(e)}`);
      setDeleteOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3, maxWidth: 900, mx: 'auto', mt: 10,
      }}
    >
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/routers')}>
            Routers
          </Button>
          <Typography variant="h6">
            {isNew ? 'New router' : (original.name || original.id)}
          </Typography>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Stack spacing={2}>
          {isNew && textField('id', {
            helperText: 'The id the signup site and Chargebee know this router by. It cannot be changed later.',
          })}
          {textField('name', { helperText: 'Shown to the customer on the signup form.' })}
          {textField('mode', { helperText: 'How the signup form groups it, e.g. Pro Wi-Fi Router.' })}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {textField('cost', { helperText: 'Dollars. 0 means no charge, and the picture is hidden.' })}
            <TextField
              select
              label={FIELD_LABELS.network}
              value={networkOfRouter(form)}
              onChange={(e) => setField('network', e.target.value)}
              helperText="Which network supplies it. A Community Fibre signup is offered that network's router."
              fullWidth
            >
              {ROUTER_NETWORKS.map((n) => (
                <MenuItem key={n.value} value={n.value}>{n.label}</MenuItem>
              ))}
            </TextField>
          </Stack>

          {textField('rsp')}

          <Divider />

          {textField('description1', { multiline: true, minRows: 2 })}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {textField('description2', { helperText: 'The line under the price, e.g. one off charge.' })}
            {textField('description3')}
          </Stack>

          <Divider />

          <FormControlLabel
            labelPlacement="start"
            sx={{ justifyContent: 'space-between', ml: 0, mr: 0 }}
            control={(
              <Switch
                checked={form.published}
                onChange={(e) => setField('published', e.target.checked)}
                color="success"
              />
            )}
            label={(
              <>
                <Typography variant="body2">{FIELD_LABELS.published}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Only published routers are offered to customers.
                </Typography>
              </>
            )}
          />

          {errors.length > 0 && dirty && (
            <Alert severity="warning">{errors.join('; ')}</Alert>
          )}

          <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
            <Button
              variant="contained"
              disabled={!dirty || errors.length > 0 || saving}
              onClick={() => setConfirmOpen(true)}
            >
              {isNew ? 'Create router…' : 'Save changes…'}
            </Button>
            <Button onClick={() => navigate('/dashboard/routers')}>Cancel</Button>
            <Box sx={{ flexGrow: 1 }} />
            {!isNew && (
              <Button color="error" disabled={saving} onClick={() => setDeleteOpen(true)}>
                Delete…
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>

      <Dialog
        open={confirmOpen}
        onClose={() => !saving && setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isNew ? `Create router "${form.id}"?` : `Apply changes to "${original.id}"?`}
        </DialogTitle>
        <DialogContent>
          {isNew ? (
            <Typography variant="body2">
              The router will be created with the values entered
              {form.published ? ' and will be offered immediately.' : ' as an unpublished draft.'}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Field</TableCell>
                  <TableCell>Current</TableCell>
                  <TableCell>New</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {changes.map((c) => (
                  <TableRow key={c.field}>
                    <TableCell>{FIELD_LABELS[c.field]}</TableCell>
                    <TableCell>{c.from}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{c.to}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>Back</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={saving}>
            {saving ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)}>
        <DialogTitle>Delete router?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {`"${original.id}" (${original.name}) will be permanently deleted.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>
            {saving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RouterCard;
